import { useRef } from "react";
import styled from "styled-components";
import { useHover } from "usehooks-ts";
import { useAppDispatch } from "../../app/hooks";
import { removeControlDeliveriesByIdsAction, removeControlDeliveryByIdAction, } from "../../app/reducers/delivery-slice";
import theme from "../../style/theme";
import { DeliveryStatus } from "../../types/delivery/delivery-status";
import { DeliveryGroupType } from "../../types/schedule/delivery-group-type";
import { PreviewDeliveryGroup } from "../../types/schedule/preview-delivery-group";
import { addressFormatByInProgressWorkPlace, addressFormatByPairWorkPlace, } from "../../utils/address/address-format";
import { timeFormat } from "../../utils/date/date-format";
import MinusFillIcon from "../icons/MinusFillIcon/MinusFillIcon";
import { SmallBadgeCard } from "./SmallBadgeCard";

interface Props {
  deliveryGroup: PreviewDeliveryGroup;
  index: number;
}

export const ScheduleDeliveryGroupCard: React.FC<Props> = (
  {
    deliveryGroup,
    index,
  }
) => {
  const dispatch = useAppDispatch();
  const hoverRef = useRef(null);
  const isHover = useHover(hoverRef);
  const isDeliveryGroupPickUp = deliveryGroup.type === DeliveryGroupType.PickUp;

  const headerAddress = addressFormatByInProgressWorkPlace(
    deliveryGroup.deliveriesInGroup
  );

  const handleClickRemoveAllButton = () => {
    if (deliveryGroup.isWait) {
      dispatch(
        removeControlDeliveriesByIdsAction({
          deliveryIds: deliveryGroup.deliveriesInGroup.map(
            (delivery) => delivery.id
          ),
        })
      );
    }
  };

  return (
    <CardContainer isWork={deliveryGroup.isWork}>
      <CardHeaderContainer
        ref={hoverRef}
        isPickUp={deliveryGroup.isPickUp}
        disabled={deliveryGroup.isComplete}
      >
        <CardHeaderTopContainer>
          {isHover && deliveryGroup.isWait ? (
            <IconContainer onClick={handleClickRemoveAllButton}>
              <MinusFillIcon style={{ width: "20px", height: "20px" }} />
            </IconContainer>
          ) : (
            <CardHeaderNumberContainer type={deliveryGroup.type}>
              <CardHeaderNumberLabel>{index + 1}</CardHeaderNumberLabel>
            </CardHeaderNumberContainer>
          )}

          <CardHeaderTypeContainer>
            <CardHeaderTypeLabel>
              {isDeliveryGroupPickUp ? "상차" : "전달"}
            </CardHeaderTypeLabel>
          </CardHeaderTypeContainer>
          <CardHeaderAddressContainer>
            <CardHeaderAddressLabel title={headerAddress}>
              {headerAddress}
            </CardHeaderAddressLabel>
          </CardHeaderAddressContainer>
        </CardHeaderTopContainer>
        <CardHeaderBottomContainer>
          <CardHeaderTimeContainer>
            <CardHeaderTimeLabel>
              {timeFormat(
                deliveryGroup.deliveriesInGroup[0].inProgressAction
                  .startTimeLimited
              )}
            </CardHeaderTimeLabel>
          </CardHeaderTimeContainer>
          <CardHeaderCountContainer>
            <CardHeaderCountLabel>
              총 {deliveryGroup.deliveriesInGroup.length}건
            </CardHeaderCountLabel>
          </CardHeaderCountContainer>
        </CardHeaderBottomContainer>
      </CardHeaderContainer>
      <CardBodyContainer>
        {deliveryGroup.deliveriesInGroup.map((delivery, deliveryIndex) => {
          const isDeliveryInGroupPickUpDisabled = isDeliveryGroupPickUp
            ? !!delivery.inProgressAction.completedAt
            : !!delivery.pairAction.completedAt;
          const isMatched = delivery.status !== DeliveryStatus.Wait;
          const itemAddress = addressFormatByPairWorkPlace(
            delivery.pairWorkPlace
          );

          const cardBodyItemLoadLabel =
            (delivery.load.name === "null" || delivery.load.name === ""
              ? "물품 현장 확인"
              : delivery.load.name) +
            (delivery.load.quantity ? ` ${delivery.load.quantity}` : '');

          return (
            <CardBodyItemContainer
              key={index}
              isPickUp={deliveryGroup.isPickUp}
              disabled={!!delivery.inProgressAction.completedAt}
            >
              <CardBodyItemTopContainer>
                <IconContainer
                  disabled={isDeliveryInGroupPickUpDisabled}
                  onClick={() =>
                    dispatch(
                      removeControlDeliveryByIdAction({
                        deliveryId: delivery.id,
                      })
                    )
                  }
                >
                  <MinusFillIcon />
                </IconContainer>
                <CardBodyItemTypeContainer>
                  <CardBodyItemTypeLabel>
                    {isDeliveryGroupPickUp ? "전달" : "상차"}
                  </CardBodyItemTypeLabel>
                </CardBodyItemTypeContainer>
                <CardBodyItemAddressContainer>
                  <CardBodyItemAddressLabel title={itemAddress}>
                    {itemAddress}
                  </CardBodyItemAddressLabel>
                </CardBodyItemAddressContainer>
              </CardBodyItemTopContainer>
              <CardBodyItemBottomContainer>
                <CardBodyItemTimeContainer>
                  <CardBodyItemTimeLabel>
                    {timeFormat(delivery.pairAction.startTimeLimited)}
                  </CardBodyItemTimeLabel>
                </CardBodyItemTimeContainer>
                <CardBodyItemLoadContainer>
                  <CardBodyItemLoadLabel title={cardBodyItemLoadLabel}>
                    {cardBodyItemLoadLabel}
                  </CardBodyItemLoadLabel>
                  {isMatched && <SmallBadgeCard label="배정완료" color="red" />}
                </CardBodyItemLoadContainer>
              </CardBodyItemBottomContainer>
            </CardBodyItemContainer>
          );
        })}
      </CardBodyContainer>
    </CardContainer>
  );
};

interface CardContainerProps {
  isWork: boolean;
}

const CardContainer = styled.div<CardContainerProps>`
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.04));
  background: ${theme.palette.primitives.white};
  border: ${(props) =>
    props.isWork && `1px solid ${theme.palette.primitives.yellow400}`};
`;

interface CardHeaderContainerProps {
  isPickUp: boolean;
  disabled: boolean;
}

const CardHeaderContainer = styled.div<CardHeaderContainerProps>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background-color: ${(props) => {
    if (props.disabled) {
      return theme.palette.primitives.gray50;
    }
    if (props.isPickUp) {
      return theme.palette.primitives.blue100;
    }
    return theme.palette.primitives.yellow100;
  }};
  padding: 10px 12px;
`;

interface CardHeaderNumberContainerProps {
  type: DeliveryGroupType;
}

const CardHeaderNumberContainer = styled.div<CardHeaderNumberContainerProps>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.type === DeliveryGroupType.PickUp
      ? theme.palette.primitives.blue400
      : theme.palette.primitives.yellow500};
  border-redius: 1px;
`;

const CardHeaderTopContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CardHeaderBottomContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 30px;
`;

const CardHeaderNumberLabel = styled.div`
  font-weight: 900;
  font-size: 13px;
  line-height: 20px;
  color: #ffffff;
`;

const CardHeaderTypeLabel = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 10px;
  color: ${theme.palette.primitives.gray900};
`;

const CardHeaderTimeLabel = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 10px;
  color: ${theme.palette.primitives.gray900};
`;

const CardHeaderCountLabel = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray400};
`;

const CardHeaderTypeContainer = styled.div`
  width: 40px;
  margin-left: 10px;
`;

const CardHeaderAddressContainer = styled.div`
  max-width: 280px;
  margin-left: 12px;
`;

const CardHeaderAddressLabel = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.palette.primitives.gray900};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardHeaderTimeContainer = styled.div`
  width: 52px;
`;

const CardHeaderCountContainer = styled.div`
  width: 78px;
`;

const CardBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

interface CardBodyItemContainerProps {
  isPickUp: boolean;
  disabled: boolean;
}

const CardBodyItemContainer = styled.div<CardBodyItemContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${(props) => {
    if (props.disabled) {
      return theme.palette.primitives.gray50;
    }
    if (props.isPickUp) {
      return theme.palette.primitives.yellow50;
    }
    return theme.palette.primitives.blue50;
  }};
  padding: 10px 12px;
`;

const CardBodyItemTopContainer = styled.div`
  display: flex;
`;

const CardBodyItemBottomContainer = styled.div`
  display: flex;
  padding-left: 21px;
  align-items: center;
`;

const CardBodyItemTypeLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};
`;

const CardBodyItemTimeLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: 0.04em;
  color: ${theme.palette.primitives.gray600};
`;

const CardBodyItemLoadLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 10px;
`;

const CardBodyItemTypeContainer = styled.div`
  width: 30px;
  padding: 0px 23px 0px 12px;
`;

const CardBodyItemAddressContainer = styled.div`
  max-width: 280px;
`;

const CardBodyItemAddressLabel = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray900};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardBodyItemTimeContainer = styled.div`
  width: 53px;
  margin-left: 8px;
`;

const CardBodyItemLoadContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: 280px;
  align-items: center;
`;

interface IconContainerProps {
  disabled?: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  cursor: pointer;
  visibility: ${(props) => (props.disabled ? "hidden" : "visible")};
`;
